import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import NoImage from "../assets/NoImage.png";

export default function ProductQuickView({
  product,
  variants = [],
  images = [],
  selectedVariantId,
  onVariantSelect,
  quantity = 1,
  onClose,
  onAdd,
  onInc,
  onDec
}) {
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  /* ---------------- DERIVED DATA ---------------- */

  const colors = [...new Set(variants.map(v => v.color))];

  const sizesForColor = selectedColor
    ? variants
      .filter(v => v.color === selectedColor)
      .map(v => v.size)
    : [];

  const selectedVariant = variants.find(
    v => v.color === selectedColor && v.size === selectedSize
  );

  /* ---------------- DEFAULT SELECTION ---------------- */

  useEffect(() => {
    if (variants.length) {
      setSelectedColor(variants[0].color);
      setSelectedSize(variants[0].size);
    }
  }, [variants]);

  useEffect(() => {
    setSelectedColor(null)
    setSelectedSize(null)
  }, [product])

  useEffect(() => {
    setActiveImage(0)
  }, [images])

  if (!product) return null;

  /* ---------------- UI ---------------- */

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">

      <div className="bg-white/40 backdrop-blur-xl border border-white/30 rounded-2xl shadow-xl w-full max-w-4xl p-4 relative mx-auto my-6 max-h-[95vh] overflow-y-auto ">

        {/* CLOSE */}
        <button
          onClick={onClose} className="sticky top-0 ml-auto flex z-20 bg-white/70 rounded-full p-2 shadow "
        >
          <X size={22} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          {/* ---------------- LEFT: IMAGE ---------------- */}
          {/* <div className="rounded-2xl bg-white/60 flex items-center justify-center overflow-hidden"> */}
          <div className="flex flex-col items-center justify-center w-full">
            {/* IMAGE SLIDER */}
            <div className="relative w-full h-80 bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center">

              {images.length > 0 ? (
                <img
                  src={images[activeImage]}
                  alt="Product"
                  className="w-full h-full object-contain transition-all duration-300"
                />
              ) : (
                <div className="text-gray-400 text-sm">No image</div>
              )}

              {/* LEFT ARROW */}
              {images.length > 1 && (
                <button
                  onClick={() =>
                    setActiveImage(
                      activeImage === 0 ? images.length - 1 : activeImage - 1
                    )
                  }
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white p-2 rounded-full shadow"
                >
                  ‹
                </button>
              )}

              {/* RIGHT ARROW */}
              {images.length > 1 && (
                <button
                  onClick={() =>
                    setActiveImage(
                      activeImage === images.length - 1 ? 0 : activeImage + 1
                    )
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white p-2 rounded-full shadow"
                >
                  ›
                </button>
              )}
            </div>

            {/* IMAGE DOTS */}
            {images.length > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition ${idx === activeImage
                      ? "bg-blue-600"
                      : "bg-gray-300 hover:bg-gray-400"
                      }`}
                  />
                ))}
              </div>
            )}

          </div>


          {/* ---------------- RIGHT: DETAILS ---------------- */}
          <div className="flex flex-col justify-between">

            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {product.productName || product.name}
              </h2>

              <p className="text-sm text-gray-600 mt-1">
                Product Name: {product.name}
              </p>
              <p className="text-xl font-bold text-gray-800 mt-3">
                ₹ {product.price}
              </p>

              {/* ---------------- COLORS ---------------- */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Select Color
                </p>

                <div className="flex flex-wrap gap-2">
                  {colors.map(c => (
                    <button
                      key={c}
                      onClick={() => {
                        setSelectedColor(c);
                        setSelectedSize(null);

                        const variant = variants.find(v => v.color === c && v.imageUrls?.length);

                        if (variant && onVariantSelect) {
                          onVariantSelect(variant);
                        }
                      }}
                      className={`px-4 py-2 rounded-full border text-sm transition
                        ${selectedColor === c
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white/70 hover:bg-white"
                        }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* ---------------- SIZES ---------------- */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Select Size
                </p>

                <div className="flex flex-wrap gap-3">
                  {sizesForColor.map(s => (
                    <button
                      key={s}
                      onClick={() => setSelectedSize(s)}
                      className={`w-12 h-12 rounded-xl border font-semibold transition
                        ${selectedSize === s
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white/70 hover:bg-white"
                        }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* ---------------- SELECTED VARIANT INFO ---------------- */}
              {/* {selectedVariant && (
                <div className="mt-4 text-sm text-gray-600">
                  Selected SKU: <span className="font-medium">{selectedVariant.sku}</span>
                </div>
              )} */}
            </div>

            {/* ---------------- FOOTER: QTY + ADD ---------------- */}
            <div className="mt-8 flex items-center justify-between">

              {/* QUANTITY */}
              <div className="flex items-center gap-4">
                <button
                  onClick={onDec}
                  className="w-10 h-10 rounded-xl bg-white/70 text-lg font-bold"
                >
                  −
                </button>

                <span className="font-semibold text-lg">{quantity}</span>

                <button
                  onClick={onInc}
                  className="w-10 h-10 rounded-xl bg-white/70 text-lg font-bold"
                >
                  +
                </button>
              </div>

              {/* ADD TO CART */}
              <button
                disabled={!selectedVariant}
                onClick={() => {
                  onAdd({
                    productId: product.id,
                    productName: product.productName || product.name,
                    variantId: selectedVariant.id,
                    sku: selectedVariant.sku,
                    color: selectedVariant.color,
                    size: selectedVariant.size,
                    price: product.price,
                    quantity,
                    imageUrl: images?.[0] || NoImage
                  });
                  onClose();
                }}
                className={`px-6 py-3 rounded-xl font-semibold transition
                  ${selectedVariant
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
              >
                Add to Cart
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
